/**
 * Helpers for GET /api/app-coins/apps (per-app rows + user total DreameyDiamonds).
 */
export const DREAMEY_VIDS_APP_ID = 2;

/**
 * Sum of DreameyDiamonds across all [main].[AppCoins] rows for the user
 * (NULL treated as 0 per row). Prefers `dreameyDiamondsSum` from the API;
 * otherwise sums the `apps` array.
 */
export function dreameyDiamondsUserSum(apiResponse) {
  if (!apiResponse || typeof apiResponse !== "object") return 0;
  const fromApi =
    apiResponse.dreameyDiamondsSum ??
    apiResponse.DreameyDiamondsSum ??
    apiResponse.totalDreameyCoinsSum ??
    apiResponse.TotalDreameyCoinsSum;
  if (fromApi != null) {
    const n = Number(fromApi);
    if (Number.isFinite(n)) return n;
  }
  const apps = apiResponse.apps;
  if (!Array.isArray(apps)) return 0;
  return apps.reduce((sum, row) => {
    const raw =
      row.dreameyDiamonds ??
      row.DreameyDiamonds ??
      row.totalDreameyCoins ??
      row.TotalDreameyCoins;
    const v = raw == null ? 0 : Number(raw);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);
}

/**
 * DreameyDiamonds for Dreamey Vids only (single app row). Prefer AppId match, then name.
 */
export function dreameyDiamondsForDreameyVids(apps) {
  if (!Array.isArray(apps)) return 0;

  const coinsFrom = (row) => {
    if (!row || typeof row !== "object") return 0;
    const raw =
      row.dreameyDiamonds ??
      row.DreameyDiamonds ??
      row.totalDreameyCoins ??
      row.TotalDreameyCoins;
    if (raw == null) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const normName = (a) => String(a?.appName ?? a?.AppName ?? "").trim().toLowerCase();

  const byId = apps.find((a) => {
    const id = Number(a?.appId ?? a?.AppId);
    return Number.isFinite(id) && id === DREAMEY_VIDS_APP_ID;
  });
  if (byId) return coinsFrom(byId);

  const exact = apps.find((a) => normName(a) === "dreamey vids");
  if (exact) return coinsFrom(exact);

  const fuzzy = apps.find((a) => {
    const n = normName(a);
    return n.includes("dreamey") && (n.includes("vid") || n.includes("vids"));
  });
  if (fuzzy) return coinsFrom(fuzzy);

  return 0;
}

/**
 * Claimed rank tier ids for Dreamey Vids from GET /app-coins/apps (ClaimedDiamonds JSON on the app row).
 */
export function parseClaimedRankRewardIdsFromAppRow(row) {
  if (!row || typeof row !== "object") return [];
  const fromApi = row.claimedRankRewardIds ?? row.ClaimedRankRewardIds;
  if (Array.isArray(fromApi)) return fromApi.filter((x) => typeof x === "string");
  const raw = row.ClaimedDiamonds;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t || t === "[]") return [];
    try {
      const j = JSON.parse(t);
      return Array.isArray(j) ? j.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function parseClaimedRankRewardIdsForDreameyVids(apps) {
  if (!Array.isArray(apps)) return [];
  const row = apps.find((a) => {
    const id = Number(a?.appId ?? a?.AppId);
    return Number.isFinite(id) && id === DREAMEY_VIDS_APP_ID;
  });
  return parseClaimedRankRewardIdsFromAppRow(row);
}

export function mergeUniqueStrings(a, b) {
  return [
    ...new Set([...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]),
  ];
}
